import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export interface ImportPreviewChapter {
  sequenceOrder: number;
  title: string;
  paragraphCount: number;
}

export interface ImportPreviewDto {
  importId: string;
  chapterCount: number;
  paragraphCount: number;
  chapters: ImportPreviewChapter[];
}

export interface ImportConfirmResult {
  chaptersInserted: number;
  paragraphsInserted: number;
}

export function useImportBook(bookId: string | undefined) {
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: async (file: File) => {
      if (!bookId) throw new Error('bookId required');
      const client = createApiClient(getToken);
      const form = new FormData();
      form.append('file', file);
      const response = await client
        .post(`api/v2/books/${bookId}/import`, { body: form, timeout: 180_000 })
        .json<ApiResponse<ImportPreviewDto>>();
      return response.data;
    },
  });
}

export function useConfirmImport(bookId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (importId: string) => {
      const client = createApiClient(getToken);
      const response = await client
        .post(`api/v2/imports/${importId}/confirm`, { json: {} })
        .json<ApiResponse<ImportConfirmResult>>();
      return response.data;
    },
    onSuccess: () => {
      if (bookId) {
        queryClient.invalidateQueries({ queryKey: ['chapters', bookId] });
        queryClient.invalidateQueries({ queryKey: ['book', bookId] });
      }
    },
  });
}
